import React, {Component} from 'react';
import {
  Autocomplete, Button, TextField, CircularProgress, Alert, IconButton, Tooltip, Chip,
  FormControlLabel, Checkbox, Typography
} from '@mui/material';
import {
  ContentCopy, CheckCircle, Error as ErrorIcon, HourglassEmpty, SkipNext
} from '@mui/icons-material';
import {connect} from 'react-redux';

import {adminCreateTenant, resumeOnboarding} from '../../actions/onboarding';
import Api from '../../api';
import {ADD_SMS_TYPE_URL, ADMIN_SETUP_TENANT_BILLING_URL} from '../../lib/constant';
import Select from '../../components/Select';
import {CREDIT_PRESETS} from './creditPresets';

// Post-onboarding setup — applied from the frontend against the same
// support APIs the tenant-details screen uses, right after the onboarding
// API returns the new org id. Deliberately NOT part of the onboarding API:
// org/user creation (Zitadel) and SMS/billing config stay separate concerns.
// The billing step re-runs AdminSetupTenantBilling with the same params the
// create call carried — it's idempotent (never tops up existing wallets), so
// it acts as a visible ensure/verify: if the inline step_billing was dropped
// or partially failed, this repairs it and the chip goes red instead of the
// tenant silently coming up on the wrong plan/wallets.
const POST_STEPS = [
  {key: 'smsTypes', label: 'SMS Types + default'},
  {key: 'billing', label: 'Billing Setup (plan + wallets)'}
];

// Listed in the order operators pick them: transactional and OTP are the
// common cases, promotional last.
const SMS_TYPE_OPTIONS = ['transactional', 'otp', 'promotional'];
const DEFAULT_TYPE_OPTIONS = [
  {value: 'transactional', name: 'Transactional'},
  {value: 'otp', name: 'OTP'},
  {value: 'promotional', name: 'Promotional'}
];
const PLAN_OPTIONS = [
  {value: 'basic', name: 'Basic'},
  {value: 'growth', name: 'Growth'},
  {value: 'pro', name: 'Pro'},
  {value: 'enterprise', name: 'Enterprise (custom rate)'}
];

const SMS_STEPS = [
  {key: 'stepCaptcha', label: 'Captcha'},
  {key: 'stepEmailCheck', label: 'DB Validation'},
  {key: 'stepDbTenant', label: 'Database Tenant'},
  {key: 'stepZitadelOrg', label: 'Create Organization'},
  {key: 'stepZitadelProjects', label: 'Setup Projects & Roles'},
  {key: 'stepZitadelUser', label: 'Create User'},
  {key: 'stepSmsConfig', label: 'SMS Configuration'},
  {key: 'stepBilling', label: 'Billing Setup'}
];

const StepIcon = ({status}) => {
  if (status === 'completed') return <CheckCircle sx={{color: 'green', fontSize: 20}} />;
  if (status === 'failed') return <ErrorIcon sx={{color: 'red', fontSize: 20}} />;
  if (status === 'skipped') return <SkipNext sx={{color: 'grey', fontSize: 20}} />;
  if (status === 'in_progress') return <CircularProgress size={18} />;
  return <HourglassEmpty sx={{color: 'grey', fontSize: 20}} />;
}

class OnboardOrg extends Component {
  constructor() {
    super();
    this.state = {
      customerName: '',
      companyName: '',
      email: '',
      mobile: '',
      address: '',
      city: '',
      dltPrincipalEntityId: '',
      senderIds: '',
      smsTypes: [],
      defaultSmsType: '',
      planCode: 'basic',
      customSmsRate: '',
      typeCredits: {},   // smsType -> initial credit count
      typeRates: {},     // smsType -> per-SMS rate override
      skipBilling: false, // tenant comes up without billing; set up later
      postSteps: null,
      submitting: false,
      result: null,
      error: null,
      copied: false
    };
    this.createReq = false;
  }

  componentWillReceiveProps(nextProps) {
    if (this.createReq && nextProps.createResult) {
      this.createReq = false;
      const isGrpcError = nextProps.createResult.code && nextProps.createResult.message;
      if (nextProps.createResult.success === false || nextProps.createResult.err || isGrpcError) {
        let errorMsg = 'Onboarding failed';
        let onboardingId = null;
        if (isGrpcError) {
          const errorInfo = nextProps.createResult.details?.find(
            d => d['@type']?.includes('ErrorInfo')
          );
          if (errorInfo?.reason === 'ORG_ALREADY_EXISTS') {
            errorMsg = `Organization "${errorInfo.metadata?.company_name || ''}" already exists. Please use a different company name.`;
          } else if (errorInfo?.reason === 'USER_ALREADY_EXISTS') {
            errorMsg = `A user with email "${errorInfo.metadata?.email || ''}" already exists. Please use a different email.`;
          } else if (errorInfo?.reason === 'ONBOARDING_PARTIAL_FAILURE') {
            onboardingId = errorInfo.metadata?.onboarding_id;
            errorMsg = nextProps.createResult.message;
          } else {
            errorMsg = nextProps.createResult.message;
            if (errorInfo?.metadata?.onboarding_id) onboardingId = errorInfo.metadata.onboarding_id;
          }
        } else {
          errorMsg = nextProps.createResult.err?.msg || nextProps.createResult.error || nextProps.createResult.message || 'Onboarding failed';
          onboardingId = nextProps.createResult.onboarding_id || nextProps.createResult.onboardingId;
        }
        this.setState({
          submitting: false,
          error: errorMsg,
          ...(onboardingId && {result: {onboardingId, status: 'failed', onboarding: nextProps.createResult.onboarding}})
        });
      } else {
        this.setState({
          submitting: false,
          result: nextProps.createResult,
          error: null
        }, () => {
          const orgId = nextProps.createResult.tenantId || nextProps.createResult.tenant_id;
          if (orgId) this.runPostSetup(orgId);
        });
      }
    }
  }

  // Phase two of the two-step onboarding. The onboarding call itself
  // (seed_profile=explicit) already applied the DLT ID (tenant config) and
  // created ALL sender ids (first = default). Only the SMS type list +
  // the admin's chosen default remain — AttachSmsTypes is idempotent
  // server-side, so overlap with the type created during onboarding is
  // fine and the default is reconciled explicitly.
  runPostSetup = async (orgId) => {
    const {smsTypes, defaultSmsType, planCode, customSmsRate, skipBilling} = this.state;
    if (!smsTypes.length && skipBilling) return;

    this.setState({postSteps: {
      ...(smsTypes.length ? {smsTypes: 'in_progress'} : {}),
      billing: skipBilling ? 'skipped' : 'pending'
    }});
    const setStep = (key, status) => this.setState(prev => ({postSteps: {...prev.postSteps, [key]: status}}));

    if (smsTypes.length) {
      try {
        await Api.put(ADD_SMS_TYPE_URL.replace('{instId}', orgId), {
          sms_types: smsTypes,
          default_sms_type: defaultSmsType || smsTypes[0]
        });
        setStep('smsTypes', 'completed');
      } catch (e) { setStep('smsTypes', 'failed'); }
    }

    if (!skipBilling) {
      setStep('billing', 'in_progress');
      try {
        const rate = planCode === 'enterprise' ? String(customSmsRate || '').trim() : '';
        await Api.post(ADMIN_SETUP_TENANT_BILLING_URL.replace('{tenantId}', orgId), {
          plan_code: planCode,
          ...(rate ? {custom_sms_rate: rate} : {}),
          sms_billing: this.buildSmsBilling()
        });
        setStep('billing', 'completed');
      } catch (e) { setStep('billing', 'failed'); }
    }
  }

  // Headers are stored UPPERCASE, and deduped case-insensitively.
  //
  // Legacy held both "DMENEW" and "DMeNew" for one tenant, and onboarding's
  // step_sms_config creates one row per entry with no folding — so the customer
  // saw the same header twice on the sender-id screen. Uppercasing at the only
  // two entry points (here and migrate-sync) makes that unrepresentable rather
  // than merely deduped.
  parsedSenderIds = () => {
    const seen = new Set();
    return this.state.senderIds
      .split(/(?:,| ,| , |, | |\n| \n|\n | \n )+/)
      .map(s => s.trim().toUpperCase())
      .filter(s => s && !seen.has(s) && seen.add(s));
  };

  // One wallet per selected SMS type: credits from the form (0 allowed —
  // wallet is still created so its rate is locked; recharge tops it up),
  // rate from the per-type override or blank = the plan's sms_sent rate.
  buildSmsBilling = () => {
    const {smsTypes, typeCredits, typeRates} = this.state;
    return smsTypes.map(t => ({
      sms_type: t,
      initial_credits: Math.max(0, parseInt(typeCredits[t], 10) || 0),
      ...(String(typeRates[t] || '').trim() ? {rate: String(typeRates[t]).trim()} : {})
    }));
  }

  submitRequest = () => {
    const {companyName, customerName, email, mobile, address, city,
      dltPrincipalEntityId, smsTypes, defaultSmsType, planCode, customSmsRate,
      typeRates, skipBilling} = this.state;
    const senders = this.parsedSenderIds();

    if (!companyName.trim()) return alert('Please enter Company Name');
    if (!customerName.trim()) return alert('Please enter Customer / Account Name');
    if (!email.trim()) return alert('Please enter Email');
    if (!mobile.trim() || mobile.length !== 10) return alert('Please enter a valid 10-digit Mobile Number');
    if (!address.trim()) return alert('Please enter Address');
    if (!city.trim()) return alert('Please enter City');
    // Complete-info onboarding (tenant_type=active, no demo seed): the SMS
    // setup fields are required for now.
    if (!dltPrincipalEntityId.trim()) return alert('Please enter DLT Principal Entity ID');
    if (!senders.length) return alert('Please enter at least one Sender ID (header)');
    if (!smsTypes.length) return alert('Please select SMS Types');
    if (!defaultSmsType) return alert('Please select Default SMS Type');
    if (!skipBilling) {
      const badRate = [customSmsRate, ...smsTypes.map(t => typeRates[t])]
        .filter(r => String(r || '').trim())
        .find(r => !/^\d+(\.\d+)?$/.test(String(r).trim()));
      if (badRate) return alert(`Invalid rate "${badRate}" — use a plain number like 0.12`);
    }

    this.createReq = true;
    this.setState({submitting: true, error: null, result: null});
    this.props.adminCreateTenant({
      companyName: companyName.trim(),
      customerName: customerName.trim(),
      email: email.trim(),
      mobile: mobile.trim(),
      address: address.trim(), city: city.trim(),
      tenantType: 'active',
      seedProfile: 'explicit',
      // Always send the welcome mail when onboarding from this form (owner
      // decision 2026-10-18). It used to be suppressed here, so every org
      // onboarded from the admin panel got no mail unless support clicked the
      // resend button on the Users tab — LAVIRE TRADING LLP (2026-10-17 22:46)
      // was told "please check mail" while nothing had been sent; the manual
      // resend went out at 22:53. `false` must be explicit: actions/onboarding.js
      // suppresses by default for any other caller (bulk migrations).
      suppressWelcomeEmail: false,
      senderIds: senders,
      dltPrincipalEntityId: dltPrincipalEntityId.trim(),
      ...(skipBilling ? {skipBilling: true} : {
        planCode,
        customSmsRate: planCode === 'enterprise' ? customSmsRate.trim() : '',
        smsBilling: this.buildSmsBilling()
      })
    });
  }

  copyCredentials = () => {
    const {result} = this.state;
    if (!result) return;
    const text = `Username: ${result.username || result.email}\nPassword: ${result.temporary_password || result.temporaryPassword}`;
    navigator.clipboard.writeText(text).then(() => {
      this.setState({copied: true});
      setTimeout(() => this.setState({copied: false}), 2000);
    });
  }

  renderPostSetupProgress() {
    const {postSteps} = this.state;
    if (!postSteps) return null;
    return (
      <div style={{margin: '16px 0'}}>
        <p style={{fontWeight: 500, marginBottom: 8}}>Post Setup (SMS Types / Billing):</p>
        {POST_STEPS.map(step => {
          const stepStatus = postSteps[step.key];
          if (!stepStatus) return null;
          return (
            <div key={step.key} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0'}}>
              <StepIcon status={stepStatus} />
              <span>{step.label}</span>
              <Chip
                label={stepStatus}
                size="small"
                variant="outlined"
                color={stepStatus === 'completed' ? 'success' : stepStatus === 'failed' ? 'error' : 'default'}
              />
            </div>
          );
        })}
        {Object.values(postSteps).includes('failed') && (
          <Alert severity="warning" sx={{mt: 1}}>
            Some post-setup steps failed — SMS steps can be re-applied from the tenant
            details screen (Edit DLT / Add Header / Add SMS Type); Billing Setup can be
            re-run from this panel (it is idempotent — existing wallets are never touched).
          </Alert>
        )}
      </div>
    );
  }

  renderStepProgress() {
    const {result} = this.state;
    if (!result || !result.onboarding) return null;
    const ob = result.onboarding;

    return (
      <div style={{margin: '16px 0'}}>
        <p style={{fontWeight: 500, marginBottom: 8}}>Onboarding Steps:</p>
        {SMS_STEPS.map(step => {
          const steps = ob.steps || ob;
          const stepStatus = steps[step.key] || 'pending';
          return (
            <div key={step.key} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0'}}>
              <StepIcon status={stepStatus} />
              <span>{step.label}</span>
              <Chip
                label={stepStatus}
                size="small"
                variant="outlined"
                color={stepStatus === 'completed' ? 'success' : stepStatus === 'failed' ? 'error' : 'default'}
              />
            </div>
          );
        })}
        {(ob.failureReason || ob.failure_reason) && (
          <Alert severity="error" sx={{mt: 1}}>
            Failed at: {ob.failureStep || ob.failure_step} — {ob.failureReason || ob.failure_reason}
          </Alert>
        )}
      </div>
    );
  }

  renderResult() {
    const {result, copied} = this.state;
    if (!result) return null;

    const username = result.username || result.email;
    const password = result.temporaryPassword || result.temporary_password;
    const status = result.status || result.overallStatus;

    return (
      <div style={{margin: '16px 0', padding: 16, background: '#f5f5f5', borderRadius: 8}}>
        <Alert severity={status === 'failed' ? 'warning' : 'success'} sx={{mb: 2}}>
          {status === 'failed'
            ? 'Onboarding partially failed. You can resume from the Onboarding History.'
            : 'Org onboarded successfully!'}
        </Alert>

        {(result.tenantId || result.tenant_id) && (
          <p style={{fontSize: 13, margin: '2px 0'}}>Org ID: {result.tenantId || result.tenant_id}</p>
        )}
        {(result.onboardingId || result.onboarding_id) && (
          <p style={{fontSize: 13, margin: '2px 0'}}>Onboarding ID: {result.onboardingId || result.onboarding_id}</p>
        )}

        {username && password && (
          <div style={{background: '#fff', padding: 12, borderRadius: 4, border: '1px solid #ddd', marginTop: 8}}>
            <p style={{margin: 0}}><strong>Username:</strong> {username}</p>
            <p style={{margin: '4px 0 0'}}><strong>Temp Password:</strong> {password}</p>
            <Tooltip title={copied ? 'Copied!' : 'Copy credentials'}>
              <IconButton onClick={this.copyCredentials} size="small" sx={{mt: 1}}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}

        {/* An "API Key (machine user PAT)" block used to sit here: it let the
            operator provision the tenant's first machine-user API key for the
            SMS channel right after onboarding, and the plan was that the SAME
            machine user would later get a WhatsApp project grant + API key
            when that channel goes live (ProvisionMachineUserApiKey already
            accepts whatsapp/email/rcs). It was removed from this screen to
            keep onboarding simple — keys are now minted from the tenant's
            API Keys tab (AdminPanel/ApiKeysTab.jsx), which calls the same
            idempotent RPC. If per-channel provisioning at onboarding time is
            needed again (e.g. when WhatsApp flips to this path), restore the
            block from git history rather than rebuilding it. */}
        {this.renderPostSetupProgress()}
        {this.renderStepProgress()}
      </div>
    );
  }

  render() {
    const {
      companyName, customerName, email, mobile,
      submitting, error, result
    } = this.state;
    const isMobile = window.isMobile();

    return (
      <div style={{
        width: isMobile ? '90%' : '50%',
        margin: '0 auto',
        paddingBottom: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: 16
      }}>
        <p style={{paddingTop: 10, fontSize: 18, fontWeight: 500}}>
          Onboard New Organization
        </p>
        <p style={{color: '#666', fontSize: 13, marginTop: -12}}>
          Creates Zitadel org + admin user + machine user (SMS) + billing org with default plans
        </p>

        {error && <Alert severity="error" onClose={() => this.setState({error: null})}>{error}</Alert>}

        {result ? (
          <>
            {this.renderResult()}
            <div>
              <Button
                onClick={() => this.setState({
                  result: null, error: null,
                  customerName: '', companyName: '', email: '', mobile: '',
                  address: '', city: '',
                  dltPrincipalEntityId: '', senderIds: '', smsTypes: [],
                  defaultSmsType: '', planCode: 'basic', customSmsRate: '',
                  typeCredits: {}, typeRates: {}, skipBilling: false, postSteps: null
                })}
                variant="outlined"
              >
                Create Another
              </Button>
              <Button onClick={() => this.props.hideSumbitRequest(true)} sx={{ml: 1}}>
                Back to List
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Company first, then the customer/account name — the latter
                becomes the admin user's name on the Zitadel org. */}
            <TextField
              value={companyName}
              fullWidth size="small" required variant="standard"
              label="Company Name"
              helperText="Becomes the organization name"
              onChange={e => this.setState({companyName: e.target.value})}
            />
            <TextField
              value={customerName}
              fullWidth size="small" required variant="standard"
              label="Customer / Account Name"
              helperText="Becomes the admin user's name"
              onChange={e => this.setState({customerName: e.target.value})}
            />
            <TextField
              value={email}
              fullWidth size="small" required variant="standard" label="Email"
              onChange={e => this.setState({email: e.target.value.trim().toLowerCase()})}
            />
            <TextField
              value={mobile}
              fullWidth size="small" required variant="standard"
              label="Mobile Number" type="number"
              onChange={e => this.setState({mobile: e.target.value})}
            />
            <TextField
              value={this.state.address}
              fullWidth size="small" required variant="standard"
              label="Address"
              onChange={e => this.setState({address: e.target.value})}
            />
            <TextField
              value={this.state.city}
              fullWidth size="small" required variant="standard"
              label="City"
              onChange={e => this.setState({city: e.target.value})}
            />

            <p style={{fontSize: 14, fontWeight: 500, margin: '12px 0 0'}}>
              SMS Setup <span style={{fontWeight: 400, color: '#666', fontSize: 12}}>
                (required)
              </span>
            </p>
            <TextField
              value={this.state.dltPrincipalEntityId}
              fullWidth size="small" required variant="standard"
              label="DLT Principal Entity ID"
              onChange={e => this.setState({dltPrincipalEntityId: e.target.value})}
            />
            <TextField
              value={this.state.senderIds}
              fullWidth size="small" required variant="standard"
              label="Sender IDs (comma separated headers)"
              onChange={e => this.setState({senderIds: e.target.value})}
            />
            <Select
              multiple={true}
              fullWidth={true}
              required
              label="SMS Types"
              value={this.state.smsTypes}
              onChange={e => this.setState({smsTypes: e.target.value})}
              options={SMS_TYPE_OPTIONS}
            />
            {this.state.smsTypes.length > 0 && (
              <Select
                fullWidth={true}
                required
                label="Default SMS Type"
                value={this.state.defaultSmsType}
                onChange={e => this.setState({defaultSmsType: e.target.value})}
                options={DEFAULT_TYPE_OPTIONS.filter(o => this.state.smsTypes.includes(o.value))}
              />
            )}

            <p style={{fontSize: 14, fontWeight: 500, margin: '12px 0 0'}}>
              Billing Setup <span style={{fontWeight: 400, color: '#666', fontSize: 12}}>
                (plan + one wallet per selected SMS type)
              </span>
            </p>
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.skipBilling}
                  onChange={e => this.setState({skipBilling: e.target.checked})}
                  size="small"
                />
              }
              label={
                <span style={{fontSize: 13}}>
                  Skip billing setup — tenant comes up with IAM + SMS config only;
                  configure billing later from this panel
                </span>
              }
            />
            {!this.state.skipBilling && (
              <>
                <Select
                  fullWidth={true}
                  label="Plan"
                  value={this.state.planCode}
                  onChange={e => this.setState({planCode: e.target.value})}
                  options={PLAN_OPTIONS}
                />
                {this.state.planCode === 'enterprise' && (
                  <TextField
                    value={this.state.customSmsRate}
                    fullWidth size="small" variant="standard"
                    label="Negotiated SMS rate (₹ per SMS, e.g. 0.10 — subscription-level override)"
                    onChange={e => this.setState({customSmsRate: e.target.value})}
                  />
                )}
                {this.state.smsTypes.map(t => (
                  <div key={t} style={{display: 'flex', gap: 12, alignItems: 'flex-end'}}>
                    <span style={{width: 110, fontSize: 13, paddingBottom: 4, textTransform: 'capitalize'}}>{t}</span>
                    <Autocomplete
                      freeSolo
                      options={CREDIT_PRESETS}
                      filterOptions={x => x}
                      getOptionLabel={o => (typeof o === 'string' ? o : o.value)}
                      renderOption={(props, o) => (
                        <li {...props} key={o.label}>
                          {o.label}&nbsp;
                          <Typography variant="body2" color="text.secondary" component="span">
                            — {Number(o.value).toLocaleString('en-IN')}
                          </Typography>
                        </li>
                      )}
                      inputValue={this.state.typeCredits[t] || ''}
                      onInputChange={(e, v) =>
                        this.setState(prev => ({typeCredits: {...prev.typeCredits, [t]: (v || '').replace(/\D/g, '')}}))
                      }
                      onChange={(e, v) => {
                        if (v && typeof v === 'object') {
                          this.setState(prev => ({typeCredits: {...prev.typeCredits, [t]: v.value}}));
                        }
                      }}
                      sx={{flex: 1}}
                      renderInput={params => (
                        <TextField {...params} size="small" variant="standard" label="Initial credits" />
                      )}
                    />
                    <TextField
                      value={this.state.typeRates[t] || ''}
                      size="small" variant="standard" sx={{flex: 1}}
                      label="Rate (blank = plan rate)"
                      onChange={e => this.setState(prev => ({typeRates: {...prev.typeRates, [t]: e.target.value}}))}
                    />
                  </div>
                ))}
              </>
            )}

            <div style={{display: 'flex', gap: 8, marginTop: 8}}>
              <Button onClick={this.submitRequest} variant="contained" disabled={submitting}>
                {submitting ? <CircularProgress size={20} sx={{mr: 1}} /> : null}
                {submitting ? 'Creating...' : 'Create Org'}
              </Button>
              <Button onClick={() => this.props.hideSumbitRequest()} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    createResult: state.onboarding.createResult
  };
}

export default connect(
  mapStateToProps,
  {adminCreateTenant, resumeOnboarding},
)(OnboardOrg);
